import { defineStore } from "pinia";
import { ref } from "vue";
import { WSClient } from "@/ems/WSClient";

interface AppContext {
    app_env: string;
    app_version: string;
    env_version: string;
    app_url: string;
    demo_series_id: number;
    new_features: string[];
    remote_charts_url: string;
    remote_stats_url: string;
    remote_automl_url: string;
    survival_chart_templates: string[];
    survival_chart_comparison_templates: string[];
    cif_chart_templates: string[];
    cif_chart_comparison_templates: string[];
    trial_days_number: number;
    feature_copilot: boolean;
    mixpanel_key: string;
    epro_reminders_unit: string;
    epro_reminders_grace_sec: number;
}
type LoadedAppContext = {
    [key in keyof AppContext]: boolean;
};

type PartialAppContext = Partial<AppContext>;

export const useAppContextStore = defineStore("app_context", () => {
    const app_context = ref<AppContext>({
        app_env: "",
        app_version: "",
        env_version: "",
        app_url: "",
        demo_series_id: 0,
        new_features: [],
        remote_charts_url: "",
        remote_stats_url: "",
        remote_automl_url: "",
        survival_chart_templates: [],
        survival_chart_comparison_templates: [],
        cif_chart_templates: [],
        cif_chart_comparison_templates: [],
        trial_days_number: 0,
        feature_copilot: false,
        mixpanel_key: "",
        epro_reminders_unit: "days",
        epro_reminders_grace_sec: 1800
    });
    const loaded_app_context = ref<LoadedAppContext>({} as LoadedAppContext);
    const loading = ref<boolean>(false);
    const loaded = ref<boolean>(false);

    const load_app_context = async (client: WSClient | undefined = undefined) => {
        if (!client) {
            client = new WSClient();
        }
        loading.value = true;
        const names = Object.keys(app_context.value);
        const envs = await load_env_params(client, names);
        app_context.value = envs;
        loading.value = false;
        loaded.value = true;
        return envs;
    };

    const load_env_params = async (client: WSClient | undefined, names: string[]) => {
        if (!client) {
            client = new WSClient();
        }
        const envs = await client.queryWs<PartialAppContext>("GET", "env_param", {
            key: names.join("|"),
        });
        for (const name of names) {
            if (name in envs) {
                Object.assign(app_context.value, {
                    [name]: castValue(envs[name as keyof PartialAppContext], name as keyof AppContext),
                });
                loaded_app_context.value[name as keyof LoadedAppContext] = true;
            }
        }

        return app_context.value;
    };
    const castValue = <K extends keyof AppContext>(value: any, key: keyof AppContext) => {
        if (typeof app_context.value[key] === "boolean") {
            return String(value) == ("true" as AppContext[K]);
        } else if (typeof app_context.value[key] === "number") {
            return Number(value) as AppContext[K];
        } else if (Array.isArray(app_context.value[key])) {
            if (!value) {
                return [];
            }
            const new_value = value.split(",").map((e: string) => e.trim());
            return (Array.isArray(new_value) ? new_value : []) as AppContext[K];
        } else {
            // String properties
            return String(value) as AppContext[K];
        }
    };

    const createGetter =
        <K extends keyof AppContext>(key: K) =>
            (): AppContext[K] => {
                if (!(loaded_app_context.value?.[key] ?? false)) {
                    load_env_params(undefined, [key]).then(() => {
                        return app_context.value[key];
                    });
                }
                return app_context.value[key];
            };

    const getters = {
        app_env: createGetter("app_env"),
        app_version: createGetter("app_version"),
        app_url: createGetter("app_url"),
        env_version: createGetter("env_version"),
        demo_series_id: createGetter("demo_series_id"),
        new_features: createGetter("new_features"),
        remote_charts_url: createGetter("remote_charts_url"),
        remote_stats_url: createGetter("remote_stats_url"),
        remote_automl_url: createGetter("remote_automl_url"),
        survival_chart_templates: createGetter("survival_chart_templates"),
        cif_chart_templates: createGetter("cif_chart_templates"),
        cif_chart_comparison_templates: createGetter("cif_chart_comparison_templates"),
        survival_chart_comparison_templates: createGetter("survival_chart_comparison_templates"),
        trial_days_number: createGetter("trial_days_number"),
        feature_copilot: createGetter("feature_copilot"),
        mixpanel_key: createGetter("mixpanel_key"),
        epro_reminders_unit: createGetter("epro_reminders_unit"),
        epro_reminders_grace_sec: createGetter("epro_reminders_grace_sec")
    };

    return {
        load_app_context,
        app_context,
        loaded,
        ...getters,
    };
});
